
import { v4 as uuidv4 } from 'uuid';
import { AdminAction } from '@/types/adminTypes';
import { getClientInfo } from '@/utils/clientInfoUtils';
import { supabase } from '@/lib/supabase';
import { loadAdminState, updateAdminState, removeActiveAdmin as removeAdmin } from './adminStateService';

const MAX_ACTIONS_TO_KEEP = 100;

/**
 * Load admin actions and state directly from Supabase with caching
 */
let cachedAdminData: { actions: AdminAction[], state: any } | null = null;
let lastDataFetch = 0;
const CACHE_TTL = 60000; // 1 minute

export const loadAdminData = async (forceRefresh = false) => {
  try {
    const now = Date.now();
    
    // Use cached data if available and not expired
    if (!forceRefresh && cachedAdminData && (now - lastDataFetch < CACHE_TTL)) {
      return cachedAdminData;
    }
    
    const { data: actions, error: actionsError } = await supabase
      .from('admin_actions')
      .select('*')
      .order('timestamp', { ascending: false })
      .limit(MAX_ACTIONS_TO_KEEP);
    
    if (actionsError) {
      return cachedAdminData || { actions: [], state: await loadAdminState() };
    }
    
    // Map database snake_case to camelCase for frontend and ensure all required fields
    const mappedActions = actions?.map(action => {
      const timestamp = action.timestamp || new Date().toISOString();
      
      return {
        actionId: action.action_id || action.actionId || `action-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        actionType: action.action_type || action.actionType || 'other',
        performedBy: action.performed_by || action.performedBy || 'Unknown',
        timestamp: timestamp,
        details: action.details || '',
        attendeeCode: action.attendee_code || action.attendeeCode,
        clientInfo: action.client_info || action.clientInfo
      };
    }) || [];
    
    const state = await loadAdminState();
    
    cachedAdminData = { actions: mappedActions, state };
    lastDataFetch = now;
    
    return cachedAdminData;
  } catch (error) {
    return cachedAdminData || { actions: [], state: await loadAdminState() };
  }
};

/**
 * Simplified filtering to only log important actions
 * This significantly reduces server load
 */
const shouldLogToDatabase = (actionType: AdminAction['actionType']): boolean => {
  // Only log login, logout, and system reset actions
  return ['login', 'logout', 'reset'].includes(actionType);
};

/**
 * Transform an AdminAction object to match Supabase's schema format
 * Converts from camelCase to snake_case for Supabase
 */
const transformActionForSupabase = (action: AdminAction) => {
  return {
    action_id: action.actionId,
    action_type: action.actionType,
    performed_by: action.performedBy,
    timestamp: action.timestamp,
    details: action.details,
    attendee_code: action.attendeeCode,
    client_info: action.clientInfo
  };
};

/**
 * Record an admin action with significant simplification
 * Only logs critically important actions to reduce database load
 */
export const recordAdminAction = async (
  actionType: AdminAction['actionType'],
  adminName: string,
  details?: string,
  attendeeCode?: string
) => {
  try {
    const now = new Date().toISOString();
    const clientInfo = getClientInfo();
    
    // Ensure adminName is not empty
    const safeAdminName = adminName || 'Unknown';
    
    const actionId = `${actionType}-${Date.now()}-${uuidv4().substring(0, 8)}`;
    
    const newAction: AdminAction = {
      actionId,
      actionType,
      performedBy: safeAdminName,
      timestamp: now,
      details,
      attendeeCode,
      clientInfo
    };
    
    // Only log important actions to the database
    if (shouldLogToDatabase(actionType)) {
      // Transform action to match Supabase schema
      const transformedAction = transformActionForSupabase(newAction);
      
      try {
        await supabase.from('admin_actions').insert([transformedAction]);
      } catch (insertError) {
        // Silent fail, continue with cached data
      }
    }
    
    // Update admin state for login/logout events
    if (['login', 'logout'].includes(actionType)) {
      const updatedState = await updateAdminState({
        lastActivity: now,
        lastSync: now
      });
      
      if (actionType === 'login' && !updatedState.activeAdmins.includes(safeAdminName)) {
        await updateAdminState({
          activeAdmins: [...updatedState.activeAdmins, safeAdminName]
        });
      }
    }
    
    // Use cached data when possible instead of new database query
    if (cachedAdminData) {
      const updatedActions = [newAction, ...cachedAdminData.actions].slice(0, MAX_ACTIONS_TO_KEEP);
      cachedAdminData.actions = updatedActions;
      
      return { 
        newAction, 
        updatedActions, 
        updatedState: cachedAdminData.state 
      };
    } else {
      // Fetch actions directly from memory or create new array
      return { 
        newAction, 
        updatedActions: [newAction], 
        updatedState: await loadAdminState() 
      };
    }
  } catch (error) {
    // Create a fallback action for UI consistency
    const now = new Date().toISOString();
    const clientInfo = getClientInfo();
    const actionId = `${actionType}-${Date.now()}-${uuidv4().substring(0, 8)}`;
    
    const newAction: AdminAction = {
      actionId,
      actionType,
      performedBy: adminName || 'Unknown',
      timestamp: now,
      details,
      attendeeCode,
      clientInfo
    };
    
    return { 
      newAction, 
      updatedActions: cachedAdminData ? 
        [newAction, ...cachedAdminData.actions].slice(0, MAX_ACTIONS_TO_KEEP) : 
        [newAction],
      updatedState: await loadAdminState()
    };
  }
};

/**
 * Remove an admin from the active list
 */
export const removeActiveAdmin = async (adminName: string) => {
  // Invalidate cache when removing admin
  cachedAdminData = null;
  return removeAdmin(adminName);
};
